import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { syncCartWithServer } from '../lib/cartSync';
import { useAuth } from '../hooks/useAuth';

export interface CartItemInput {
  id: string;
  kind?: 'release' | 'track' | 'tip';
  tipForItemId?: string;
  tipSource?: 'artist' | 'cart-item';
  releaseId?: number;
  releaseTitle?: string;
  title: string;
  artist: string;
  artistSlug: string;
  artistId?: number;
  releaseSlug?: string;
  cover: string | null;
  trackId?: number;
  trackTitle?: string;
  previewTrackId?: number;
  price: number;
  isPWYW: boolean;
  minPrice?: number;
  isLimited?: boolean;
  limitedLeft?: number;
  isBundle?: boolean;
  isMemberOnly?: boolean;
  formats?: string[];
  extras?: string[];
  quantity?: number;
}

export interface CartItem extends CartItemInput {
  kind: 'release' | 'track' | 'tip';
  tipForItemId?: string;
  tipSource?: 'artist' | 'cart-item';
  releaseId: number;
  releaseTitle?: string;
  trackTitle?: string;
  quantity: number;
  formats: string[];
  extras: string[];
}

export interface SavedItem extends CartItemInput {}

interface CartContextValue {
  items: CartItem[];
  savedItems: SavedItem[];
  addItem: (item: CartItemInput) => void;
  updateItemPrice: (id: string, price: number) => void;
  updateItemQuantity: (id: string, quantity: number) => void;
  removeItem: (id: string) => void;
  saveItemForLater: (id: string) => void;
  moveSavedToCart: (id: string) => void;
  removeSavedItem: (id: string) => void;
  clearCart: () => void;
  hydrateFromBootstrap: (items: CartItemInput[], savedItems?: SavedItem[]) => void;
}

const CART_ITEMS_KEY = '254tracks.cart.items';
const SAVED_ITEMS_KEY = '254tracks.cart.saved';

const CartContext = createContext<CartContextValue | undefined>(undefined);

function readFromStorage<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch (error) {
    console.warn(`Failed to read cart storage key "${key}"`, error);
    return fallback;
  }
}

function writeToStorage<T>(key: string, value: T) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn(`Failed to persist cart storage key "${key}"`, error);
  }
}

const extractNumericId = (value: string | number | undefined): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const match = value.match(/(\d+)$/);
    if (match) {
      return Number(match[1]);
    }
  }
  return null;
};

const withDefaults = (item: CartItemInput): CartItem => {
  const inferredKind = item.kind ?? (item.trackId ? 'track' : 'release');
  const parsedId = extractNumericId(item.id);
  const releaseId =
    item.releaseId ??
    (inferredKind === 'release' ? parsedId ?? null : item.releaseId ?? null) ??
    0;
  const normalizedId =
    inferredKind === 'track'
      ? item.id.startsWith('track-')
        ? item.id
        : `track-${item.trackId ?? parsedId ?? item.id}`
      : inferredKind === 'tip'
        ? (item.id.startsWith('tip-') ? item.id : `tip-${item.artistId ?? item.artistSlug ?? item.id}`)
        : item.id.startsWith('release-')
          ? item.id
          : `release-${releaseId || parsedId || item.id}`;

  return {
    ...item,
    id: normalizedId,
    kind: inferredKind,
    releaseId: releaseId,
    releaseTitle: item.releaseTitle ?? item.title,
    trackTitle: item.trackTitle ?? item.title,
    cover: item.cover && item.cover.trim() ? item.cover : null,
    isPWYW: false,
    minPrice: undefined,
    quantity: item.quantity ?? 1,
    formats: item.formats ?? [],
    extras: item.extras ?? [],
  };
};

export function CartProvider({ children }: { children: ReactNode }) {
  const sanitizeItems = (entries: CartItemInput[]): CartItem[] =>
    entries.map((entry) => withDefaults({ ...entry, isPWYW: false, minPrice: undefined }));

  const [items, setItems] = useState<CartItem[]>(() =>
    sanitizeItems(readFromStorage<CartItemInput[]>(CART_ITEMS_KEY, []))
  );
  const [savedItems, setSavedItems] = useState<SavedItem[]>(() =>
    sanitizeItems(readFromStorage<SavedItem[]>(SAVED_ITEMS_KEY, []))
  );
  const { token } = useAuth();
  const { pathname } = useLocation();
  const skipSyncRef = useRef(
    typeof window !== 'undefined' &&
      (window as { __HOME_BOOTSTRAP__?: boolean }).__HOME_BOOTSTRAP__ === true
  );

  useEffect(() => {
    writeToStorage(CART_ITEMS_KEY, items);
  }, [items]);

  useEffect(() => {
    writeToStorage(SAVED_ITEMS_KEY, savedItems);
  }, [savedItems]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isAdminRoute = pathname.startsWith('/admin');
    if (isAdminRoute) return;
    const isExploreRoute = pathname.startsWith('/explore');
    const isHomeRoute = pathname === '/';
    const isArtistsRoute = pathname.startsWith('/artists');
    const isReleasesRoute = pathname.startsWith('/releases');
    if (isExploreRoute || isHomeRoute || isArtistsRoute || isReleasesRoute) return;
    if (skipSyncRef.current) {
      skipSyncRef.current = false;
      return;
    }
    const sync = async () => {
      try {
        await syncCartWithServer({
          items,
          token,
          allowEmpty: true,
        });
      } catch (error) {
        console.warn('Failed to sync cart with server', error);
      }
    };
    void sync();
  }, [items, token, pathname]);

  const addItem = useCallback((item: CartItemInput) => {
    const nextItem = withDefaults(item);
    setItems((prev) => {
      const index = prev.findIndex((entry) => entry.id === nextItem.id);
      if (index >= 0) {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          ...nextItem,
          quantity: nextItem.quantity ?? updated[index].quantity,
        };
        return updated;
      }
      return [...prev, nextItem];
    });
  }, []);

  const updateItemPrice = useCallback((id: string, price: number) => {
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, price } : item))
    );
  }, []);

  const updateItemQuantity = useCallback((id: string, quantity: number) => {
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, quantity } : item))
    );
  }, []);

  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const saveItemForLater = useCallback((id: string) => {
    setItems((prevItems) => {
      const target = prevItems.find((item) => item.id === id);
      if (!target) return prevItems;
      setSavedItems((prevSaved) => {
        if (prevSaved.some((saved) => saved.id === target.id)) {
          return prevSaved;
        }
        const { quantity, ...rest } = target;
        return [...prevSaved, { ...rest, quantity }];
      });
      return prevItems.filter((item) => item.id !== id);
    });
  }, []);

  const moveSavedToCart = useCallback((id: string) => {
    setSavedItems((prevSaved) => {
      const target = prevSaved.find((item) => item.id === id);
      if (!target) return prevSaved;
      setItems((prevItems) => {
        const normalized = withDefaults({ ...target, quantity: target.quantity ?? 1 });
        const existing = prevItems.find((item) => item.id === target.id);
        if (existing) {
          return prevItems.map((item) => (item.id === target.id ? normalized : item));
        }
        return [...prevItems, normalized];
      });
      return prevSaved.filter((item) => item.id !== id);
    });
  }, []);

  const removeSavedItem = useCallback((id: string) => {
    setSavedItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const hydrateFromBootstrap = useCallback(
    (nextItems: CartItemInput[], nextSavedItems: SavedItem[] = []) => {
      skipSyncRef.current = true;
      setItems(sanitizeItems(nextItems));
      if (nextSavedItems.length > 0) {
        setSavedItems(sanitizeItems(nextSavedItems));
      }
    },
    []
  );

  const value = useMemo<CartContextValue>(
    () => ({
      items,
      savedItems,
      addItem,
      updateItemPrice,
      updateItemQuantity,
      removeItem,
      saveItemForLater,
      moveSavedToCart,
      removeSavedItem,
      clearCart,
      hydrateFromBootstrap,
    }),
    [
      items,
      savedItems,
      addItem,
      updateItemPrice,
      updateItemQuantity,
      removeItem,
      saveItemForLater,
      moveSavedToCart,
      removeSavedItem,
      clearCart,
      hydrateFromBootstrap,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return ctx;
}
